import React, { useState, useEffect } from 'react';
import { Button, NumberInput, InlineLoading } from 'carbon-components-react';
import { Information24 } from '@carbon/icons-react';
import { connect, useDispatch } from 'react-redux';
import { RootState } from 'redux/root.reducer';
import { CFCProps } from 'types';
import {
  updateOrgProfileStart,
  updateOrgProfileUpdateFlag,
  fetchOrgProfileStart
} from 'redux/member/action';
import { setLastVisitListPage } from 'redux/areaOfInterest/actions';
import { useComponentMount } from 'utilities/hooks.util';
import * as Yup from 'yup';

const ManageBuffer: React.FC<Props> = ({
  orgProfile,
  submitting,
  orgProfileUpdated,
  errorMsg,
  lastVisitListPage,
  organisationId
}) => {
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isSaveEnabled, setIsSaveEnabled] = useState<boolean>(false);
  const [aoiBuffer, setAoiBuffer] = useState<number | undefined>(undefined);
  const [invalidMsg, setInvalidMsg] = useState<string | undefined>();
  const dispatch = useDispatch();

  const handleNumberInputChange = (event: any) => {
    const generalErrMsg =
      'Please enter a valid buffer distance between 5m and 25m';
    const schema = Yup.number()
      .integer(generalErrMsg)
      .min(5, generalErrMsg)
      .max(25, generalErrMsg);
    const newValue = Number(event.imaginaryTarget.value);
    let myError = undefined;
    try {
      schema.validateSync(newValue);
    } catch (e) {
      if (e instanceof Yup.ValidationError) {
        myError = e.message;
      }
    }
    setInvalidMsg(myError);
    setAoiBuffer(newValue);
    setIsSaveEnabled(!myError);
  };

  const handleSubmit = () => {
    const payload = {
      aoiBuffer
    };
    const params = { organisationId: Number(organisationId), payload };
    dispatch(updateOrgProfileStart(params));
  };

  useEffect(() => {
    if (orgProfileUpdated) {
      setIsSuccess(true);
    }
  }, [orgProfileUpdated]);

  useEffect(() => {
    if (isSuccess) {
      dispatch(
        fetchOrgProfileStart({
          organisationId: Number(organisationId),
          params: {
            include: 'Tenant'
          }
        })
      );
      if (!lastVisitListPage) {
        // force history to refresh
        dispatch(setLastVisitListPage(1));
      }
      setTimeout(() => {
        setIsSuccess(false);
      }, 2000);
    }
  }, [isSuccess, dispatch, organisationId, lastVisitListPage]);

  useEffect(() => {
    if (orgProfile) {
      setAoiBuffer(orgProfile.aoiBuffer || 25);
    }
  }, [orgProfile]);

  useComponentMount(() => {
    return () => {
      dispatch(updateOrgProfileUpdateFlag(false));
    };
  });

  return (
    <div className="manage-aoi-buffer-container">
      <div className="manage-aoi-buffer-content">
        <div className="aoi-buffer-input-container">
          <NumberInput
            id="aoi-buffer-input"
            label="Buffer (in metres)"
            max={aoiBuffer ? 25 : undefined}
            min={aoiBuffer ? 5 : undefined}
            step={5}
            invalid={!!errorMsg || !!invalidMsg}
            invalidText={errorMsg || invalidMsg}
            helperText="Min. value : 5 m. Max. value: 25 m"
            value={aoiBuffer || 25}
            size={'xl'}
            onChange={handleNumberInputChange}
            disabled={submitting}
          />
        </div>
        <div className="aoi-buffer-info-container">
          <div className="aoi-buffer-info-icon">
            <Information24 />
          </div>
          <div className="aoi-buffer-info-title">
            Understanding AOI buffer distance
          </div>
          <div className="aoi-buffer-info-text">
            The AOI buffer defines how close an enquiry dig site must be to your
            Area of Interest before you’ll receive a referral. Referrals are
            only triggered when the dig site falls within this buffer distance.
          </div>
        </div>
      </div>
      <div className="manage-aoi-buffer-footer">
        <Button
          id="submit"
          name="submit"
          type="submit"
          kind="primary"
          data-testid="submit"
          onClick={() => handleSubmit()}
          disabled={!isSaveEnabled || submitting || isSuccess}
        >
          {!submitting && !isSuccess ? (
            `Save`
          ) : (
            <InlineLoading
              description={isSuccess ? 'Saved' : 'Saving ...'}
              status={isSuccess ? 'finished' : 'active'}
              aria-live={'assertive'}
            />
          )}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = ({ memberCache, aoiCache }: RootState) => ({
  orgProfile: memberCache.orgProfile,
  submitting: memberCache.submitting,
  errorMsg: memberCache.error,
  lastVisitListPage: aoiCache.lastVisitListPage,
  orgProfileUpdated: memberCache.orgProfileUpdated
});

const connector = connect(mapStateToProps);

type ownProps = {
  organisationId: string;
};

type Props = CFCProps<typeof connector> & ownProps;

ManageBuffer.displayName = 'ManageAOIBuffer';

export default React.memo(connector(ManageBuffer));
